/**
 * Column definitions for the Reports > Top Gifts table (GFG-272).
 *
 * Fixed arrangement: `COLUMN_ORDER`/`COLUMN_LABELS` are the whole story — no ColumnConfigPopover,
 * no sort, no per-column config. This table isn't rendered by the shared `Blotter` component (see
 * `TopGiftsTable.tsx`), so `TopGiftColumn` only carries `key`/`label`/`render` plus optional cell
 * styles — no `sortable`/`sortValue`.
 *
 * `orDash` (EMPTY_CELL in the table) and `orBlank` ("" in the export) come from
 * `utils/reportsRedemptions-helper.ts` rather than being redeclared here.
 */
import CardGiftcardOutlined from "@mui/icons-material/CardGiftcardOutlined";
import type { ReactNode } from "react";
import { COLUMN_LABELS, COLUMN_ORDER, THUMBNAIL_SIZE } from "@/constants/reportsTopGifts";
import type { TopGiftColumn, TopGiftRow } from "@/interfaces/reportsTopGifts";
import type { CsvColumn } from "@/lib/csv";
import { orBlank, orDash } from "@/utils/reportsRedemptions-helper";
import { formatPct, pctBarWidth } from "@/utils/reportsTopGifts-helper";

/** The rank cell — a single large number, no medal/icon per the ticket. */
function renderRank(row: TopGiftRow) {
  return <span className="font-crimson text-[22px] font-semibold">{row.rank}</span>;
}

/** Thumbnail (or a placeholder square with a gift icon when there's no image) plus the title. */
function renderProduct(row: TopGiftRow) {
  return (
    <div className="flex items-center gap-3">
      {row.giftImg ? (
        // alt="" is deliberate: the image is decorative, the product title sits right next to it.
        <img
          src={row.giftImg}
          alt=""
          width={THUMBNAIL_SIZE}
          height={THUMBNAIL_SIZE}
          className="rounded-lg object-cover"
        />
      ) : (
        <div
          className="bg-wax rounded-lg flex items-center justify-center"
          style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
        >
          <CardGiftcardOutlined />
        </div>
      )}
      <span className="font-semibold">{row.productTitle}</span>
    </div>
  );
}

/** The cause cell — em-dash for `null`, same convention as the Redemptions/Bounces tables. */
function renderCause(row: TopGiftRow) {
  return <>{orDash(row.cause)}</>;
}

/** The redemptions cell — locale-formatted count, no decimals. */
function renderRedemptions(row: TopGiftRow) {
  return <>{row.redemptions.toLocaleString()}</>;
}

/** The pct cell — formatted percentage text plus a proportional bar underneath it. */
function renderPct(row: TopGiftRow) {
  return (
    <div className="flex items-center gap-2">
      <span>{formatPct(row.pct)}</span>
      <div role="presentation" className="h-2 flex-1 rounded bg-wax">
        <div className="h-2 rounded bg-dark-dust" style={{ width: pctBarWidth(row.pct) }} />
      </div>
    </div>
  );
}

const RENDER: Record<TopGiftColumn["key"], (row: TopGiftRow) => ReactNode> = {
  rank: renderRank,
  product: renderProduct,
  cause: renderCause,
  redemptions: renderRedemptions,
  pct: renderPct,
};

/** The 5 table columns, in `COLUMN_ORDER`, each labelled from `COLUMN_LABELS`. */
export function buildTopGiftColumns(): TopGiftColumn[] {
  return COLUMN_ORDER.map((key) => ({
    key,
    label: COLUMN_LABELS[key],
    render: RENDER[key],
  }));
}

const CSV_VALUE: Record<TopGiftColumn["key"], (row: TopGiftRow) => string> = {
  rank: (row) => String(row.rank),
  product: (row) => row.productTitle,
  cause: (row) => orBlank(row.cause),
  redemptions: (row) => String(row.redemptions),
  pct: (row) => formatPct(row.pct),
};

/**
 * The 5 CSV columns, in the ticket's exact order (independent of `COLUMN_ORDER`). Labels are read
 * from `COLUMN_LABELS` — the single source of truth shared with the table header — not
 * re-literalled here.
 */
const CSV_COLUMN_ORDER: TopGiftColumn["key"][] = ["rank", "product", "cause", "redemptions", "pct"];

export function buildTopGiftCsvColumns(): CsvColumn<TopGiftRow>[] {
  return CSV_COLUMN_ORDER.map((key) => ({
    label: COLUMN_LABELS[key],
    value: CSV_VALUE[key],
  }));
}
